
import { Component, Prop, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  LeaveRequestInterface,
  LeavesRepositoryInterface,
  RequestCancellationInterface,
} from '@/services/Calendar/leaves/interfaces';
import {
  BaseProfileDataInterface,
  ProfileInterface,
  ProfileRepositoryInterface,
} from '@/services/Users-profile/interfaces';
import FileRepositoryInterface from "@/services/File-manager/interfaces/file.interface";
import { FileEntity, FolderEntity } from '@fenix/file-manager-entities/file-manager-entities/entitites';
import Docxtemplater from 'docxtemplater';
import JSZip from 'jszip';
import JSZipUtils from 'jszip-utils';
import saveAs from 'file-saver';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'profileRepository',
    'leavesRepository',
    'fileRepository',
  ],
})
export default class ViewLeaveRequestDialog extends Vue {
  @Prop() private leaveRequest!: LeaveRequestInterface;

  private visible: boolean = true;
  private loading: boolean = true;

  private leavesRepository!: LeavesRepositoryInterface;
  private fileRepository!: FileRepositoryInterface;

  private profileRepository!: ProfileRepositoryInterface;
  private usersMap: { [userId: string]: BaseProfileDataInterface } = {};
  private userProfile!: ProfileInterface;

  public async created(): Promise<void> {
    await this.getProfilesData();
    this.loading = false;
  }

  private async getProfilesData(): Promise<void> {
    const usersIds: string[] = [];
    if (!usersIds.includes(this.leaveRequest.requestBy)) {
      usersIds.push(this.leaveRequest.requestBy);
    }

    if (!usersIds.includes(this.leaveRequest.firstApprover)) {
      usersIds.push(this.leaveRequest.firstApprover);
    }

    if (!usersIds.includes(this.leaveRequest.secondApprover)) {
      usersIds.push(this.leaveRequest.secondApprover);
    }

    if (
      this.leaveRequest.cancellation
      && this.leaveRequest.cancellation.reviewedBy
      && !usersIds.includes(this.leaveRequest.cancellation.reviewedBy)
    ) {
      usersIds.push(this.leaveRequest.cancellation.reviewedBy);
    }

    if (this.leaveRequest.reviewedBy && !usersIds.includes(this.leaveRequest.reviewedBy)) {
      usersIds.push(this.leaveRequest.reviewedBy);
    }

    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(usersIds);
    profiles.forEach((profile) => {
      this.usersMap[profile.userId] = profile;
    });

    this.userProfile = await this.profileRepository.getOneByUserId(this.$store.getters.id);
  }

  private async cancelRequest(): Promise<void> {
    try {
      await this.leavesRepository.createCancellationRequest(this.leaveRequest.id);
      this.$successDialog.show('Cancellation Request Created Successful');
      this.closeComponent();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message);
    }
  }

  private async downloadDocument(request: LeaveRequestInterface): Promise<void> {
    const file = new FileEntity();
    const folder = new FolderEntity();
    folder.private = true;
    file.name = this.generateLeaveDocumentName(request);
    file.folder = folder;

    if (request.file != null) {
      file.path = request.file;
    }

    await this.fileRepository.downloadFile(file);
  }

  private generateLeaveDocumentName(request: LeaveRequestInterface): string {
    const serialNumber = this.generateLeaveDocumentNumber(request);
    const name = this.usersMap[request.requestBy].fullName
      ? this.usersMap[request.requestBy].fullName
      : `${this.usersMap[request.requestBy].firstName} ${this.usersMap[request.requestBy].lastName}`;

    return `${serialNumber} - ${name} - ${request.dateFrom}`;
  }

  private generateLeaveDocumentNumber(request: LeaveRequestInterface): string {
    const serialNumber = '0'.repeat(6 - String(request.id).length) + request.id;
    return `${request.leaveType.name[0]}${serialNumber}`;
  }

  private getLeaveStatus(leaveRequest: LeaveRequestInterface): string {
    if (leaveRequest.cancellation && leaveRequest.cancellation.isApproved) {
      return 'Cancelled';
    }

    if (leaveRequest.isApproved === null) {
      return 'Pending';
    } else if (leaveRequest.isApproved) {
      return 'Approved';
    } else {
      return 'Denied';
    }
  }

  private getCancellationStatus(cancellation: RequestCancellationInterface): string {
    if (cancellation.isApproved === null) {
      return 'Pending';
    } else if (cancellation.isApproved) {
      return 'Approved';
    } else {
      return 'Denied';
    }
  }

  private closeComponent(): void {
    this.visible = false;
    this.$emit('close');
  }

  private generateDocument = (leaveRequest: LeaveRequestInterface): void => {
    this.createAndSaveDocument(leaveRequest);
  };

  private loadFile(url: any, callback: any): void {
    JSZipUtils.getBinaryContent(url, callback);
  }

  private createAndSaveDocument(leaveRequest: LeaveRequestInterface): void {
    const dataset = {
      requestNumber: this.generateLeaveDocumentNumber(leaveRequest),
      realName: `${this.userProfile.personalDetails.firstName} ${this.userProfile.personalDetails.lastName}`,
      department: this.userProfile.companyDetails.department,
      leaveDays: leaveRequest.days,
      leaveType: leaveRequest.leaveType.translatedName,
      startDate: new Date(leaveRequest.dateFrom).toLocaleString('en-gb').substr(0, 10),
      endDate: new Date(leaveRequest.dateTo).toLocaleString('en-gb').substr(0, 10),
      requestedOn: new Date(leaveRequest.requestOn).toLocaleString('en-gb').substr(0, 10),
      article: leaveRequest.leaveType.article ? leaveRequest.leaveType.article : '',
    };
    const filename = this.generateLeaveDocumentName(leaveRequest);
    this.loadFile('/document-templates/leaveRequestTemplate.docx', (error: any, content: any): void => {
      if (error) {
        throw error;
      }

      const zip = JSZip(content);
      const doc = new Docxtemplater().loadZip(zip);
      doc.setData(dataset);
      try {
        doc.render();
      } catch (error) {
        const e = {
          message: error.message,
          name: error.name,
          stack: error.stack,
          properties: error.properties,
        };
        console.log(JSON.stringify({ error: e }));
        throw error;
      }

      const out: Blob = doc.getZip().generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

      saveAs(out, `${filename}.docx`);    // You can pass this blob to a custom file saver component in the project.
    });
  }
}
